import { z } from "zod";
import { SOURCE_REF_KINDS } from "./ideation-maps.js";
/** Exact stored rel only — do not canonicalise PART_OF / CONTAINS. */
export const COMPOSITION_SCOPE_RELS = ["part-of"];
export const CONNECTED_SCOPE_MAX_ORIGINS = 12;
export const CONNECTED_MAX_FACTS_PER_ORIGIN = 20;
export const CONNECTED_MAX_FACTS_TOTAL = 80;
export const CONNECTED_MAX_DECISIONS_PER_ORIGIN = 20;
export const CONNECTED_MAX_EVIDENCE_REFS_PER_ORIGIN = 20;
export const ConnectedScopeKindSchema = z.enum(["explicit", "composition"]);
export const ConnectedScopePathSchema = z.object({
    mechanism: ConnectedScopeKindSchema,
    recordId: z.string().min(1),
    rel: z.string().min(1),
    fromId: z.string().min(1),
    toId: z.string().min(1),
});
export const ConnectedScopeOriginSchema = z.object({
    originSubjectId: z.string().min(1),
    scopeKind: ConnectedScopeKindSchema,
    path: ConnectedScopePathSchema,
});
export const ConnectedEvidenceRefSchema = z.object({
    citationId: z.string().min(1),
    kind: z.enum(SOURCE_REF_KINDS),
    ref: z.string().min(1),
    span: z.string().nullable(),
});
export const ConnectedFactSchema = z.object({
    originSubjectId: z.string().min(1),
    citationId: z.string().min(1),
    key: z.string().min(1),
    value: z.string().min(1),
    unit: z.string().nullable(),
    asOf: z.string().min(1),
    source: z.string().min(1),
    recordedAt: z.string(),
    recordedBy: z.string(),
    evidence: z.array(ConnectedEvidenceRefSchema),
});
export const ConnectedDecisionSchema = z.object({
    originSubjectId: z.string().min(1),
    citationId: z.string().min(1),
    text: z.string().min(1),
    at: z.string().min(1),
    by: z.string(),
});
export const ConnectedOriginProjectionSchema = z.object({
    originSubjectId: z.string().min(1),
    label: z.string(),
    kind: z.string(),
    scopeKind: ConnectedScopeKindSchema,
    path: ConnectedScopePathSchema,
    superseded: z.boolean(),
    facts: z.array(ConnectedFactSchema),
    decisions: z.array(ConnectedDecisionSchema),
    truncatedFacts: z.boolean(),
});
export const ConnectedKnowledgeBudgetsSchema = z.object({
    maxOrigins: z.number().int().min(0),
    maxFactsPerOrigin: z.number().int().min(0),
    maxFactsTotal: z.number().int().min(0),
});
export const ConnectedConflictSchema = z.object({
    key: z.string().min(1),
    originSubjectIds: z.array(z.string().min(1)).min(2),
});
export const ConnectedKnowledgeProjectionSchema = z.object({
    currentSubjectId: z.string().min(1),
    origins: z.array(ConnectedOriginProjectionSchema),
    facts: z.array(ConnectedFactSchema),
    conflicts: z.array(ConnectedConflictSchema),
    truncated: z.boolean(),
    budgets: ConnectedKnowledgeBudgetsSchema,
});
export const SubjectKnowledgeScopeSchema = z.object({
    id: z.string().uuid(),
    currentSubjectId: z.string().uuid(),
    originSubjectId: z.string().uuid(),
    createdAt: z.string().datetime(),
    createdBy: z.string().email(),
    archivedAt: z.string().datetime().nullable(),
});
export const SubjectKnowledgeScopeCreateSchema = z.object({
    originSubjectId: z.string().uuid(),
});
export const KnowledgeScopeIdParamSchema = z.object({
    id: z.string().uuid(),
});
export function connectedEvidenceCitationId(originSubjectId, kind, ref) {
    return `connected-evidence:${originSubjectId}:${kind}:${ref}`;
}
export function connectedFactCitationId(originSubjectId, factCitationId) {
    return `connected-fact:${originSubjectId}:${factCitationId}`;
}
export function connectedDecisionCitationId(originSubjectId, decisionCitationId) {
    return `connected-decision:${originSubjectId}:${decisionCitationId}`;
}
export function sortConnectedFacts(facts) {
    return [...facts].sort((a, b) => a.key.localeCompare(b.key) ||
        a.asOf.localeCompare(b.asOf) ||
        a.citationId.localeCompare(b.citationId));
}
export function sortConnectedDecisions(decisions) {
    return [...decisions].sort((a, b) => a.at.localeCompare(b.at) || a.citationId.localeCompare(b.citationId));
}
export function emptyConnectedKnowledge(currentSubjectId) {
    return {
        currentSubjectId,
        origins: [],
        facts: [],
        conflicts: [],
        truncated: false,
        budgets: {
            maxOrigins: CONNECTED_SCOPE_MAX_ORIGINS,
            maxFactsPerOrigin: CONNECTED_MAX_FACTS_PER_ORIGIN,
            maxFactsTotal: CONNECTED_MAX_FACTS_TOTAL,
        },
    };
}
export function isCompositionScopeRel(rel) {
    return COMPOSITION_SCOPE_RELS.includes(rel);
}
/**
 * Directed child → parent only. Exact `rel` match; PART_OF / CONTAINS never match.
 */
export function matchCompositionScopeEdge(input) {
    if (!isCompositionScopeRel(input.rel))
        return null;
    if (input.fromId === input.toId)
        return null;
    if (input.toId !== input.currentSubjectId)
        return null;
    return { originSubjectId: input.fromId };
}
function factIdentityValue(fact) {
    return `${fact.value}\u001f${fact.unit ?? ""}`;
}
export function sortConnectedScopeOrigins(origins) {
    return [...origins].sort((a, b) => {
        if (a.scopeKind !== b.scopeKind)
            return a.scopeKind === "explicit" ? -1 : 1;
        return a.originSubjectId.localeCompare(b.originSubjectId);
    });
}
export function dedupeConnectedScopeOrigins(origins) {
    const byOrigin = new Map();
    for (const origin of origins) {
        const existing = byOrigin.get(origin.originSubjectId);
        if (!existing) {
            byOrigin.set(origin.originSubjectId, origin);
            continue;
        }
        if (existing.scopeKind === "explicit")
            continue;
        if (origin.scopeKind === "explicit") {
            byOrigin.set(origin.originSubjectId, origin);
            continue;
        }
        if (origin.path.recordId < existing.path.recordId) {
            byOrigin.set(origin.originSubjectId, origin);
        }
    }
    return sortConnectedScopeOrigins([...byOrigin.values()]);
}
export function capConnectedScopeOrigins(origins, max = CONNECTED_SCOPE_MAX_ORIGINS) {
    if (origins.length <= max) {
        return { origins, truncated: false, omittedOriginCount: 0 };
    }
    return {
        origins: origins.slice(0, max),
        truncated: true,
        omittedOriginCount: origins.length - max,
    };
}
export function collectConnectedConflicts(facts) {
    const byKey = new Map();
    for (const fact of facts) {
        const origins = byKey.get(fact.key) ?? new Map();
        if (!origins.has(fact.originSubjectId)) {
            origins.set(fact.originSubjectId, factIdentityValue(fact));
        }
        byKey.set(fact.key, origins);
    }
    const conflicts = [];
    for (const [key, origins] of [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        const identities = [...new Set(origins.values())];
        if (identities.length < 2)
            continue;
        conflicts.push({
            key,
            originSubjectIds: [...origins.keys()].sort((a, b) => a.localeCompare(b)),
        });
    }
    return conflicts;
}
export function applyConnectedFactBudgets(origins) {
    let remaining = CONNECTED_MAX_FACTS_TOTAL;
    let truncated = false;
    const next = [];
    for (const origin of origins) {
        const limited = origin.facts.slice(0, CONNECTED_MAX_FACTS_PER_ORIGIN);
        const perOriginTruncated = limited.length < origin.facts.length;
        const taken = remaining <= 0 ? [] : limited.slice(0, remaining);
        if (taken.length < limited.length || perOriginTruncated)
            truncated = true;
        remaining -= taken.length;
        next.push({
            ...origin,
            facts: taken,
            truncatedFacts: perOriginTruncated || taken.length < limited.length,
        });
    }
    return { origins: next, facts: next.flatMap((origin) => origin.facts), truncated };
}
/**
 * Stable CP-visible connected projection. Excludes recordedAt, path record
 * ids, conflicts, budgets, and assemble timestamps.
 */
export function connectedContentIdentityInput(connected) {
    const origins = [...connected.origins]
        .sort((a, b) => a.originSubjectId.localeCompare(b.originSubjectId))
        .map((origin) => ({
        originSubjectId: origin.originSubjectId,
        label: origin.label,
        kind: origin.kind,
        scopeKind: origin.scopeKind,
        superseded: origin.superseded,
        truncatedFacts: origin.truncatedFacts,
        facts: sortConnectedFacts(origin.facts).map((fact) => ({
            citationId: fact.citationId,
            key: fact.key,
            value: fact.value,
            unit: fact.unit,
            asOf: fact.asOf,
            source: fact.source,
            evidence: [...fact.evidence]
                .sort((a, b) => a.citationId.localeCompare(b.citationId))
                .map((row) => ({
                citationId: row.citationId,
                kind: row.kind,
                ref: row.ref,
                span: row.span,
            })),
        })),
        decisions: sortConnectedDecisions(origin.decisions).map((decision) => ({
            citationId: decision.citationId,
            text: decision.text,
            at: decision.at,
        })),
    }));
    return { truncated: connected.truncated, origins };
}
export function applyConnectedDecisionBudgets(origins) {
    let truncated = false;
    const next = origins.map((origin) => {
        const limited = origin.decisions.slice(0, CONNECTED_MAX_DECISIONS_PER_ORIGIN);
        if (limited.length < origin.decisions.length)
            truncated = true;
        return { ...origin, decisions: limited };
    });
    return { origins: next, truncated };
}
//# sourceMappingURL=subject-connected-knowledge.js.map